import { ERROR_MESSAGES } from '@/data/constants'

interface ErrorContext {
  endpoint?: string
  method?: string
  data?: {
    message?: string
    details?: unknown
  }
}

/**
 * Gets a user-friendly error message based on HTTP status and context
 */
export const getContextualErrorMessage = (
  status: number,
  context: ErrorContext = {},
  fallbackMessage?: string
): string => {
  const { endpoint, method, data } = context

  // If we have a custom message from the API, use it for certain cases
  // "not found" messages from the API are usually specific and user-friendly
  if (data?.message) {
    // Always show specific "not found" messages from the API (e.g., "Contact group not found")
    if (/not found/i.test(data.message) && !isGenericNotFoundMessage(data.message)) {
      return data.message
    }
    // For other messages, check if they're technical
    if (!isTechnicalMessage(data.message)) {
      return data.message
    }
  }
  
  // Context-specific 404 messages
  if (status === 404 && endpoint) {
    if (endpoint.includes('/teams/')) {
      return ERROR_MESSAGES.TEAM_NOT_FOUND
    }
    if (endpoint.includes('/users/')) {
      return ERROR_MESSAGES.USER_NOT_FOUND
    }
    if (endpoint.includes('/channels/')) {
      return ERROR_MESSAGES.CHANNEL_NOT_FOUND
    }
    if (endpoint.includes('/business/')) {
      return ERROR_MESSAGES.BUSINESS_NOT_FOUND
    }
    if (endpoint.includes('/invitations/')) {
      return ERROR_MESSAGES.INVITATION_NOT_FOUND
    }
    if (endpoint.includes('/roles/')) {
      return ERROR_MESSAGES.ROLE_NOT_FOUND
    }
    if (endpoint.includes('/upload/')) {
      return ERROR_MESSAGES.UPLOAD_FAILED
    }
    
    // Generic endpoint not found
    return ERROR_MESSAGES.ENDPOINT_NOT_FOUND
  }
  
  // Context-specific 400 messages
  if (status === 400 && endpoint) {
    if (endpoint.includes('/upload/')) {
      return ERROR_MESSAGES.UPLOAD_FAILED
    }
    if (method === 'POST' && endpoint.includes('/save/')) {
      return ERROR_MESSAGES.SAVE_FAILED
    }
    if (method === 'PUT' || method === 'PATCH') {
      return ERROR_MESSAGES.UPDATE_FAILED
    }
    if (method === 'DELETE') {
      return ERROR_MESSAGES.DELETE_FAILED
    }
  }
  
  // Context-specific 500 messages
  if (status >= 500 && endpoint) {
    if (endpoint.includes('/upload/')) {
      return ERROR_MESSAGES.UPLOAD_FAILED
    }
    if (method === 'GET') {
      return ERROR_MESSAGES.LOAD_FAILED
    }
    if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
      return ERROR_MESSAGES.SAVE_FAILED
    }
    if (method === 'DELETE') {
      return ERROR_MESSAGES.DELETE_FAILED
    }
  }
  
  // Default HTTP status messages
  switch (status) {
    case 400:
      return ERROR_MESSAGES.BAD_REQUEST
    case 401:
      return ERROR_MESSAGES.UNAUTHORIZED
    case 403:
      return ERROR_MESSAGES.FORBIDDEN
    case 404:
      return ERROR_MESSAGES.NOT_FOUND
    case 409:
      return ERROR_MESSAGES.CONFLICT
    case 410:
      return ERROR_MESSAGES.GONE
    case 413:
      return ERROR_MESSAGES.PAYLOAD_TOO_LARGE
    case 415:
      return ERROR_MESSAGES.UNSUPPORTED_MEDIA_TYPE
    case 429:
      return ERROR_MESSAGES.TOO_MANY_REQUESTS
    case 500:
      return ERROR_MESSAGES.SERVER_ERROR
    case 502:
      return ERROR_MESSAGES.BAD_GATEWAY
    case 503:
      return ERROR_MESSAGES.SERVICE_UNAVAILABLE
    case 504:
      return ERROR_MESSAGES.GATEWAY_TIMEOUT
    default:
      return fallbackMessage || ERROR_MESSAGES.UNKNOWN_ERROR
  }
}

/**
 * Checks if a "not found" message is generic and should be replaced
 * Returns true for messages like "Not Found", "404 Not Found", "Resource not found"
 * Returns false for specific messages like "Contact group not found", "Template not found"
 */
const isGenericNotFoundMessage = (message: string): boolean => {
  const genericPatterns = [
    /^not found$/i,
    /^404$/i,
    /^404 not found$/i,
    /^resource not found$/i,
    /^endpoint not found$/i,
    /^route not found$/i,
    /^page not found$/i,
  ]
  return genericPatterns.some(pattern => pattern.test(message.trim()))
}

/**
 * Checks if a message is technical and should be replaced with user-friendly text.
 * Only matches genuinely technical messages (stack traces, HTTP status codes, internal errors).
 * User-friendly API messages like "Cannot remove the last member" should pass through.
 */
const isTechnicalMessage = (message: string): boolean => {
  const technicalPatterns = [
    // Generic HTTP status messages
    /^internal server error$/i,
    /^bad request$/i,
    /^unauthorized$/i,
    /^forbidden$/i,
    /^service unavailable$/i,
    /^bad gateway$/i,
    /^gateway timeout$/i,
    /^\d{3}\s/,                       // Messages starting with HTTP status codes like "500 ..."
    // Network / transport errors
    /ECONNREFUSED/,
    /ECONNRESET/,
    /ETIMEDOUT/,
    /ENOTFOUND/,
    /failed to fetch/i,
    /network error/i,
    /cors error/i,
    // Stack traces and code errors
    /stack trace/i,
    /at \w+\s+\(/,                    // Stack frame like "at Function (/path...)"
    /unexpected token/i,
    /syntax error/i,
    /cannot read propert/i,           // "Cannot read property / properties of undefined"
    /is not a function/i,
    /null pointer/i,
    /segmentation fault/i,
  ]

  return technicalPatterns.some(pattern => pattern.test(message))
}

/**
 * Gets operation-specific error message based on HTTP method
 */
export const getOperationErrorMessage = (method: string): string => {
  switch (method?.toUpperCase()) {
    case 'GET':
      return ERROR_MESSAGES.LOAD_FAILED
    case 'POST':
      return ERROR_MESSAGES.SAVE_FAILED
    case 'PUT':
    case 'PATCH':
      return ERROR_MESSAGES.UPDATE_FAILED
    case 'DELETE':
      return ERROR_MESSAGES.DELETE_FAILED
    default:
      return ERROR_MESSAGES.OPERATION_FAILED
  }
}

/**
 * Gets endpoint-specific error message based on URL path
 */
export const getEndpointErrorMessage = (endpoint: string): string => {
  if (!endpoint) return ERROR_MESSAGES.ENDPOINT_NOT_FOUND
  
  const path = endpoint.toLowerCase()
  
  if (path.includes('upload')) {
    return ERROR_MESSAGES.UPLOAD_FAILED
  }
  
  if (path.includes('team')) {
    return ERROR_MESSAGES.TEAM_NOT_FOUND
  }
  
  if (path.includes('user')) {
    return ERROR_MESSAGES.USER_NOT_FOUND
  }
  
  if (path.includes('channel')) {
    return ERROR_MESSAGES.CHANNEL_NOT_FOUND
  }
  
  if (path.includes('business')) {
    return ERROR_MESSAGES.BUSINESS_NOT_FOUND
  }
  
  if (path.includes('invitation')) {
    return ERROR_MESSAGES.INVITATION_NOT_FOUND
  }
  
  if (path.includes('role')) {
    return ERROR_MESSAGES.ROLE_NOT_FOUND
  }
  
  return ERROR_MESSAGES.ENDPOINT_NOT_FOUND
}